import { useState } from "react";
import type { PoseOptions } from "../../../shared/api-contract";
import { useViewer } from "../../../state/ViewerProvider";
import { useDomainState } from "../../../state/editor/useEditor";
import { MiniBtn } from "../inspector/rows";


/** Poses panel (iJewel PosePlugin): PCA auto-orient + drop-to-ground.
 *
 * The "Auto update ground" checkbox was deleted in b50b391 as a stored-never-read
 * control, but its state, its toggle handler and — the part no compiler flag
 * catches — a live getPoseAutoUpdateGround() call on every mount were left
 * behind. That read could surface "Could not read the pose settings." for a
 * control the user cannot see. All three are gone. */
export function PosesPanel() {
  const { client } = useViewer();
  const r = client.ring;
  const models = useDomainState("models");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** Both buttons run the same RPC; `id` keeps the spinner on the one clicked
   * (a single shared flag put "…" on the sibling too) while disabling both, so
   * a second pose cannot be computed on top of one in flight. */
  const pose = async (id: string, opts?: PoseOptions) => {
    if (busy) return;
    setBusy(id);
    setError(null);
    try {
      await r.computePose(opts);
    } catch (e) {
      // computePose rejects on a viewer with no loadable geometry; unhandled,
      // the button just snapped back and looked like it had worked.
      setError(e instanceof Error ? e.message : "Could not compute the pose.");
    } finally {
      setBusy(null);
    }
  };

  const noModel = models.length === 0;

  return (
    <div className="flex flex-col gap-3 p-3">
      <span className="text-[12px] font-semibold text-ed-text-2">Auto pose</span>
      <div className="flex flex-wrap gap-1.5">
        {/* Disabled with nothing loaded: computePose on an empty scene is a
            silent no-op, which reads as a broken button. */}
        <MiniBtn primary icon="compass_calibration" busy={busy === "orient"} disabled={!!busy || noModel} onClick={() => void pose("orient")}>
          Compute
        </MiniBtn>
        <MiniBtn icon="vertical_align_bottom" busy={busy === "drop"} disabled={!!busy || noModel} onClick={() => void pose("drop", { orient: false, dropToGround: true })}>
          Drop to Ground
        </MiniBtn>
      </div>
      {noModel ? <span className="text-[11px] leading-snug text-ed-text-3">Load a model to pose it.</span> : null}
      {error ? <span className="text-[11px] leading-snug text-ed-red">{error}</span> : null}
      <p className="text-[11px] leading-snug text-ed-text-3">PCA orients the model (longest axis horizontal) and rests it on the ground plane.</p>
    </div>
  );
}
